import React from 'react'
import { flexRender } from '@tanstack/react-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useDocumentTableHook } from '../../hooks/document.table.hook'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import {
  TableWrapper,
  DownloadAllButton,
  StyledTbody,
  StyledTd,
  PageInput,
  SelectPageEntries,
} from './table.styles'
import { Wrapper, Typography } from '../globals'
import { Document } from '../../../domain/documents.types'
import PaginationButton from './components/PaginationButton'
import ActionGroupButton from './components/actionGroupButton'
import SortableTableHeader from './components/sortableTableHeader'
import SearchInput from './components/searchInput'
import { CustomColumnMeta } from './table.types'

export type DocumentsTableProps = {
  data: Document[]
  columns: any
  viewFunction: (...args: any[]) => void
  downloadFunction: (...args: any[]) => void
  downloadMultipleFunction: (...args: any[]) => void
  isDownloading: boolean
  setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>
}

const DocumentsTable: React.FC<DocumentsTableProps> = (props) => {
  const {
    data,
    columns,
    viewFunction,
    downloadFunction,
    downloadMultipleFunction,
    isDownloading,
    setIsDownloading
  } = props

  const { t } = useCustomTranslation('common')

  const {
    tableInstance,
    inputPage,
    setInputPage,
    NUMB_OF_ENTRIES,
    rowSelection,
    pagination,
    rowCount,
    searchInput,
    setSearchInput,
    setSearchQuery,
    isLoading,
  } = useDocumentTableHook({ data, columns, setIsDownloading })

  return (
    <>
      <TableWrapper>
        <Wrapper
          display="flex"
          flex-direction="column"
          width="95%"
          margin="10px"
          height="auto"
          marginBottom="40px"
        >
          <Wrapper alignItems="baseline" width="60%">
            <SelectPageEntries
              value={pagination.pageSize}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                tableInstance.setPageSize(Number(e.target.value))
              }
            >
              {NUMB_OF_ENTRIES.map((pageSize) => {
                return (
                  <option
                    key={pageSize}
                    value={pageSize}
                    disabled={tableInstance.getState().pagination.pageSize === pageSize}
                  >
                    {pageSize}
                  </option>
                )
              })}
            </SelectPageEntries>
            <Typography>entrées par page</Typography>
          </Wrapper>
          <Wrapper alignItems="baseline">
            <SearchInput
              label="Rechercher"
              value={searchInput || ''}
              placeholder={t('search_by_titled_or_power_plant')}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              setSearchQuery={setSearchQuery}
              tableInstance={tableInstance}
              isLoading={isLoading}
            />
          </Wrapper>
          <Wrapper justifyContent="end">
            {isLoading && (
              <>
                <Wrapper display="flex" alignItems="center" gap="0.5rem" justifyContent="end">
                  <FontAwesomeIcon icon="spinner" size="1x" color="white" spin />
                  <Typography>{t('loading')}</Typography>
                </Wrapper>
              </>
            )}
            {Object.keys(rowSelection).length !== 0 && !isDownloading ? (
              <DownloadAllButton
                type="button"
                onClick={() => downloadMultipleFunction(rowSelection)}
              >
                {t('download_selection')}
              </DownloadAllButton>
            ) : null}
          </Wrapper>
        </Wrapper>

        {data.length === 0 ? (
          <p>{t('no_document_found')}</p>
        ) : (
          <TableWrapper>
            <table>
              <thead style={{ height: '3.5rem' }}>
                {tableInstance.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <SortableTableHeader
                        key={header.id}
                        label={
                          flexRender(header.column.columnDef.header, header.getContext()) as string
                        }
                        onClick={(e: React.MouseEvent) => {
                          if (header.column.columnDef.id !== 'select') {
                            tableInstance.resetPageIndex()
                            header.column.getToggleSortingHandler()?.(e)
                          }
                        }}
                        isLoading={isLoading}
                        sort={header.column.getIsSorted()}
                        columnId={header.column.columnDef.id}
                        meta={header.column.columnDef.meta}
                      />
                    ))}
                  </tr>
                ))}
              </thead>
              <StyledTbody>
                {tableInstance.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) =>
                      cell.column.columnDef.id !== 'action' ? (
                        <StyledTd
                          key={cell.id}
                          align={
                            cell.column.columnDef.meta
                              ? (cell.column.columnDef.meta as CustomColumnMeta).textAlign
                              : 'left'
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </StyledTd>
                      ) : (
                        <ActionGroupButton
                          document={row.original as Document}
                          downloadFunction={downloadFunction}
                          viewFunction={viewFunction}
                          isDownloading={isDownloading}
                        />
                      )
                    )}
                  </tr>
                ))}
              </StyledTbody>
            </table>
          </TableWrapper>
        )}

        {data.length > 0 && (
          <Wrapper
            marginTop="3rem"
            display="flex"
            justifyContent="space-around"
            height="auto"
            width="90%"
          >
            <Wrapper width="25%">
              <Typography alignSelf="center">
                Document {pagination.pageIndex * pagination.pageSize + 1} à{' '}
                {pagination.pageIndex * pagination.pageSize +
                  tableInstance.getRowModel().rows.length}{' '}
                sur {rowCount}
              </Typography>
            </Wrapper>

            <Wrapper justifyContent="left" marginLeft="1rem">
              <Wrapper width="auto">
                <PaginationButton
                  onClick={() => tableInstance.firstPage()}
                  disabled={!tableInstance.getCanPreviousPage()}
                  label="<<"
                />
              </Wrapper>
              <Wrapper width="auto">
                <PaginationButton
                  onClick={() => tableInstance.previousPage()}
                  disabled={!tableInstance.getCanPreviousPage()}
                  label="<"
                />
              </Wrapper>
              <Wrapper width="auto">
                <PaginationButton
                  onClick={() => tableInstance.nextPage()}
                  disabled={!tableInstance.getCanNextPage()}
                  label=">"
                />
              </Wrapper>
              <Wrapper width="auto">
                <PaginationButton
                  onClick={() => tableInstance.lastPage()}
                  disabled={!tableInstance.getCanNextPage()}
                  label=">>"
                />
              </Wrapper>
              <Wrapper marginLeft="1rem" width="auto">
                <Typography alignSelf="center">
                  Page {pagination.pageIndex + 1} sur {tableInstance.getPageCount()} - allez à la
                  page :
                </Typography>
              </Wrapper>
              <Wrapper
                marginLeft="1rem"
                width="70px"
                height="35px"
                marginTop="5px"
                border="1px solid #85f6f4"
                borderRadius="6px"
              >
                <PageInput
                  type="number"
                  min="1"
                  max={tableInstance.getPageCount()}
                  value={inputPage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value
                    setInputPage(value === '' ? '' : Number(value))
                  }}
                  onBlur={() => {
                    const page = inputPage ? Number(inputPage) - 1 : 0
                    tableInstance.setPageIndex(page)
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      const page = inputPage ? Number(inputPage) - 1 : 0
                      tableInstance.setPageIndex(page)
                    }
                  }}
                />
              </Wrapper>
            </Wrapper>
          </Wrapper>
        )}
      </TableWrapper>
    </>
  )
}

export default DocumentsTable
